// src/oura/merchant/MerchantDrawers.tsx
//
// The merchant's one drawer for what needs them, and the report drawer.
// ClientPage mounts this provider once around whichever view is showing;
// screens reach it through `useMerchantDrawers` (useMerchantDrawers.ts).
//
// One shell, one keyed sheet inside it (specs v1.1, A-merchant host): a
// question (QuestionSheet) or a shared document (DocSheet). An item that is
// in the Needs-you queue opens with a snapshot of the queue, so "2 of 9" and
// the arrows walk it; anything else opens alone. A write completes the item
// when it has left the queue on the next read, and the foot then offers the
// next one ("Sent to Angel. 8 more need you." and Next). Nothing moves on by
// itself.
//
// The unsent answers are held here, per question (a document's questions by
// docAnswerKey, behind the Docs write switch), and in sessionStorage past
// a reload, so moving on, closing, or going to another screen loses nothing.
// The documents are read here once for every screen, kept fresh on the one
// shared timer (useMerchantDocuments), so every count agrees.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { readViewAs } from "@util/viewAs";
import { markReportRead, type ClientPagePayload } from "@service/aeo/clientReview";
import { useSnackbar } from "@hook/use-snackbar";
import { Drawer, DrawerBlock, DrawerHead, drawerNav, drawerSequence, type DrawerDone, type DrawerReturnFocus, type DrawerSeqItem } from "@oura/ui";
import { useMerchantDocuments } from "../hooks/useMerchantDocuments";
import { asksOf, askGroup, draftDecision, isOpen, plural, type AnswerDraft } from "./asks";
import { needsYouQueue, queueKey, type QueueEntry } from "./docAsks";
import { DocSheet } from "./Documents";
import { focusAfterEmptied } from "./drawers/answersEmptied";
import { QuestionSheet } from "./drawers/QuestionSheet";
import { ReportDrawer } from "./drawers/ReportDrawer";
import type { SheetFlow } from "./drawers/sheetFlow";
import { MerchantDrawersContext, type MerchantDrawers } from "./useMerchantDrawers";

export interface MerchantDrawersProviderProps {
    /** The page the drawers render from; fresh after every refresh. */
    payload: ClientPagePayload;
    /** Reads the page again past the shared cache. Every write in a drawer ends with it. */
    onRefresh: () => Promise<void>;
    children: ReactNode;
}

type ItemKind = QueueEntry["kind"];

type OpenDrawer =
    /** `seq`: the queue's keys when it opened (queueKey), or null for an item opened alone. */
    | { kind: ItemKind; id: string; seq: string[] | null }
    | { kind: "report"; reportId: string };

const keyOf = (kind: ItemKind, id: string) => queueKey({ kind, id });

function parseKey(key: string): { kind: ItemKind; id: string } {
    return { kind: key.startsWith("q:") ? "question" : "doc", id: key.slice(2) };
}

/**
 * Unsent answers, per question, mirrored to sessionStorage so a reload keeps
 * them for the rest of the visit. Keyed by business: a staffer previewing two
 * merchants in one tab must not carry one's drafts into the other.
 *
 * sessionStorage throws in some private windows and when site data is
 * blocked; the drafts then last as long as the page does, which is fine.
 */
function useAnswerDrafts(businessId: string): [Record<string, AnswerDraft>, (itemId: string, draft: AnswerDraft | null) => void] {
    const key = `oura.merchant.answerDrafts.${businessId}`;
    const [drafts, setDrafts] = useState<Record<string, AnswerDraft>>(() => {
        try {
            const raw = window.sessionStorage.getItem(key);
            const parsed: unknown = raw ? JSON.parse(raw) : null;
            return parsed && typeof parsed === "object" ? (parsed as Record<string, AnswerDraft>) : {};
        } catch {
            return {};
        }
    });
    useEffect(() => {
        try {
            window.sessionStorage.setItem(key, JSON.stringify(drafts));
        } catch {
            /* the drafts still live in memory */
        }
    }, [key, drafts]);
    const setDraft = useCallback((itemId: string, draft: AnswerDraft | null) => {
        setDrafts((prev) => {
            const next = { ...prev };
            if (draft) next[itemId] = draft;
            else delete next[itemId];
            return next;
        });
    }, []);
    return [drafts, setDraft];
}

/** A control focus can land on: on the page, not hidden, not inside a folded card. */
function reachable(el: Element | null): HTMLElement | null {
    if (!(el instanceof HTMLElement) || !el.isConnected) return null;
    if (el.closest("[inert],[hidden]") || el.getClientRects().length === 0) return null;
    return el;
}

export function MerchantDrawersProvider({ payload, onRefresh, children }: MerchantDrawersProviderProps) {
    const snackbar = useSnackbar();
    const { documents: docList, refreshFailed, reads, refresh } = useMerchantDocuments(payload);
    // One object while nothing in it changes, so the screens' effects that read it do not re-run.
    const docs = useMemo(() => ({ documents: docList, refreshFailed, reads, refresh }), [docList, refreshFailed, reads, refresh]);
    const [open, setOpen] = useState<OpenDrawer | null>(null);
    const [drafts, setDraft] = useAnswerDrafts(payload.business.id);
    /** What the last write in the open item did, until another item shows. */
    const [said, setSaid] = useState<{ key: string; text: string } | null>(null);
    const [busy, setBusy] = useState(false);

    const asks = useMemo(() => asksOf(payload.packs), [payload.packs]);
    const documents = docs.documents;
    const queue = useMemo(() => needsYouQueue({ packs: payload.packs, documents }), [payload.packs, documents]);
    const queueTitles = useMemo(() => new Map(queue.map((e) => [queueKey(e), e.title])), [queue]);
    // An answer counts once it would send: a draft still missing its words does not.
    const readyDraftIds = useMemo(
        () => new Set(asks.filter((a) => isOpen(a.item) && draftDecision(a.item, drafts[a.item.id])).map((a) => a.item.id)),
        [asks, drafts],
    );

    /** Titles seen, so an item that has gone still has a title to show. */
    const seenTitles = useRef(new Map<string, string>());
    queue.forEach((e) => seenTitles.current.set(queueKey(e), e.title));

    /**
     * Set when the drawer closes on Done with nothing left: focus goes to the
     * screen's steady place (useAnswersEmptiedFocus), since the rows it
     * opened from have gone.
     */
    const emptied = useRef(false);
    /** The item last on show, read at close time for where focus goes. */
    const shown = useRef<{ kind: ItemKind; id: string } | null>(null);
    const latest = useRef({ asks, documents });
    latest.current = { asks, documents };

    const show = useCallback((kind: ItemKind, id: string, seq: string[] | null) => {
        setSaid(null);
        setBusy(false);
        shown.current = { kind, id };
        setOpen({ kind, id, seq });
    }, []);

    const value = useMemo<MerchantDrawers>(() => {
        const snapshot = () => queue.map(queueKey);
        const inQueue = (kind: ItemKind, id: string) => queueTitles.has(keyOf(kind, id));
        return {
            openQuestion(itemId) {
                if (!asks.some((a) => a.item.id === itemId)) return;
                show("question", itemId, inQueue("question", itemId) ? snapshot() : null);
            },
            openDoc(docId) {
                if (!documents?.some((d) => d.id === docId)) return;
                show("doc", docId, inQueue("doc", docId) ? snapshot() : null);
            },
            openQueue(startId) {
                if (queue.length === 0) {
                    snackbar.showSuccess("0 pending", "We will tell you when something needs you.");
                    return;
                }
                const start = (startId ? queue.find((e) => e.id === startId) : undefined) ?? queue[0];
                show(start.kind, start.id, snapshot());
            },
            openReport(reportId) {
                if (!payload.reports.some((r) => r.id === reportId)) return;
                // Opening a report IS reading it, wherever it was opened from:
                // the Reports list, a link into one, or the Work screen. Here
                // rather than at a caller, so a new way in cannot forget it
                // and leave the sidebar claiming the report is still new.
                // Staff viewing as the merchant must not clear the merchant's marks (#4112).
                if (!readViewAs().active) void markReportRead(reportId);
                setOpen({ kind: "report", reportId });
            },
            draftCount: readyDraftIds.size,
            readyDraftIds,
            queue,
            docs,
        };
    }, [asks, documents, queue, queueTitles, payload.reports, snackbar, readyDraftIds, docs, show]);

    /**
     * Where focus goes on close: the item's row, then the head of the group it
     * now sits in, then the card that lists it; failing all three, the drawer
     * goes back to whatever opened it. After a Done that left nothing, the
     * screen's steady place.
     */
    const returnFocus = useCallback<DrawerReturnFocus>(() => {
        if (emptied.current) {
            emptied.current = false;
            const steady = focusAfterEmptied();
            if (steady) return steady;
        }
        const item = shown.current;
        if (!item) return null;
        const { asks: nowAsks, documents: nowDocs } = latest.current;
        const esc = CSS.escape(item.id);
        const selectors: string[] = [];
        if (item.kind === "question") {
            const ask = nowAsks.find((a) => a.item.id === item.id);
            selectors.push(`[data-ask-id="${esc}"]`);
            if (ask) selectors.push(`[data-group-head="asks.${askGroup(ask.item)}"]`);
            selectors.push("#asks h2 button");
        } else {
            const doc = nowDocs?.find((d) => d.id === item.id);
            selectors.push(`[data-doc-id="${esc}"]`);
            if (doc) selectors.push(`[data-group-head="documents.${doc.state}"]`);
            selectors.push("#documents h2 button");
        }
        selectors.push("#needs-reply h2 button");
        for (const s of selectors) {
            for (const el of Array.from(document.querySelectorAll(s))) {
                const target = reachable(el);
                if (target) return target;
            }
        }
        return null;
    }, []);

    const readyCount = readyDraftIds.size;
    const close = useCallback(() => {
        setOpen(null);
        setSaid(null);
        setBusy(false);
        if (readyCount > 0) snackbar.showSuccess(`Kept. ${plural(readyCount, "answer")} not sent yet.`);
    }, [readyCount, snackbar]);

    // The item on show, and where it sits in the queue.
    const item = open && open.kind !== "report" ? open : null;
    const currentKey = item ? keyOf(item.kind, item.id) : null;
    const ask = item?.kind === "question" ? asks.find((a) => a.item.id === item.id) ?? null : null;
    const doc = item?.kind === "doc" ? documents?.find((d) => d.id === item.id) ?? null : null;
    const inQueue = currentKey !== null && queueTitles.has(currentKey);
    // The walk counts against the list as it was when it started: an answered
    // item keeps its place, so "3 of 9" never restarts at "1 of 8", and
    // Previous goes back to it.
    const titleOf = (k: string) => queueTitles.get(k) ?? seenTitles.current.get(k) ?? null;
    const sequence = item?.seq && currentKey ? drawerSequence(item.seq, currentKey, titleOf) : null;

    /**
     * In a walk, the snapshot's next item still waiting: never back round to
     * one already passed (a document opened stays in the queue until its
     * replies are read, and would come round again forever). At the end of
     * the walk, or opened alone, the live queue's head, so a Done that says
     * "3 more need you" always has a Next.
     */
    const nextKey = (() => {
        if (!currentKey) return null;
        if (item?.seq && sequence && sequence.index >= 0) {
            const after = item.seq.slice(item.seq.indexOf(currentKey) + 1).find((k) => queueTitles.has(k));
            if (after) return after;
        }
        const head = queue.find((e) => queueKey(e) !== currentKey);
        return head ? queueKey(head) : null;
    })();
    const next: DrawerSeqItem | null = nextKey ? { id: nextKey, title: queueTitles.get(nextKey) ?? "" } : null;
    const left = queue.length - (inQueue ? 1 : 0);

    const moveTo = useCallback(
        (key: string) => {
            if (!item) return;
            const { kind, id } = parseKey(key);
            // A next item from outside the snapshot starts a fresh one, so the arrows still work.
            const seq = item.seq && item.seq.includes(key) ? item.seq : queue.map(queueKey);
            show(kind, id, seq);
        },
        [item, queue, show],
    );

    const nav = sequence ? drawerNav(sequence, { group: "Needs you", onMove: (key) => moveTo(key), disabled: busy }) : undefined;

    const finish = useCallback(() => {
        emptied.current = left === 0;
        close();
    }, [left, close]);

    const completed = Boolean(said && said.key === currentKey && !inQueue);
    const done: DrawerDone | null = completed && said
        ? {
            said: said.text,
            left,
            group: "Needs you",
            next,
            audience: "merchant",
            onNext: () => nextKey && moveTo(nextKey),
            onDone: finish,
            // What is left, by name, so the last Done never says "3 more" without saying which.
            leftNames: queue.filter((e) => queueKey(e) !== currentKey).map((e) => e.title),
        }
        : null;

    const flow: SheetFlow = {
        said: (text: string) => {
            if (currentKey) setSaid({ key: currentKey, text });
        },
        setBusy,
        done,
        message: said && said.key === currentKey && !completed ? said.text : null,
        inQueue,
        next,
        goNext: () => {
            if (nextKey) moveTo(nextKey);
        },
        finish,
        clear: () => setSaid(null),
    };

    const report = open?.kind === "report" ? payload.reports.find((r) => r.id === open.reportId) ?? null : null;
    const threadOf = (itemId: string) => payload.threads.find((t) => t.itemId === itemId) ?? null;

    let sheet: ReactNode = null;
    if (item && ask) {
        sheet = (
            <QuestionSheet
                key={currentKey}
                ask={ask}
                thread={threadOf(ask.item.id)}
                payload={payload}
                draft={drafts[ask.item.id]}
                setDraft={setDraft}
                onRefresh={onRefresh}
                flow={flow}
            />
        );
    } else if (item && doc) {
        sheet = (
            <DocSheet
                key={currentKey}
                doc={doc}
                payload={payload}
                flow={flow}
                refreshFailed={docs.refreshFailed}
                onRetry={docs.refresh}
                drafts={drafts}
                setDraft={setDraft}
            />
        );
    } else if (item && currentKey) {
        // Withdrawn, or no longer shared: say so, and let the arrows move on.
        sheet = (
            <Gone
                key={currentKey}
                title={seenTitles.current.get(currentKey) ?? (item.kind === "question" ? "This question" : "This document")}
            />
        );
    }

    return (
        <MerchantDrawersContext.Provider value={value}>
            {children}
            <Drawer open={item !== null} onClose={close} nav={nav} itemKey={currentKey ?? undefined} returnFocus={returnFocus}>
                {sheet}
            </Drawer>
            <ReportDrawer key={report?.id} report={report} onClose={() => setOpen(null)} />
        </MerchantDrawersContext.Provider>
    );
}

function Gone({ title }: { title: string }) {
    return (
        <>
            <DrawerHead title={title} />
            <DrawerBlock>
                <p className="m-0">This is no longer open.</p>
            </DrawerBlock>
        </>
    );
}
